import {
	faDatabase,
	faSitemap,
	faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { ObjectService } from '@supply-mover-2-0/core';
import { useEffect, useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { SelectSearch } from '../../../../components/forms/SelectSearch';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { useInjectedFields } from '../../../registry/InjectedFieldRegistry';
import type {
	DataSourceType,
	FieldConfiguration,
	ListBuilderConfiguration,
} from '../../../types';
import { useListBuilderContext } from '../../core/ListBuilderProvider';
import { TemplateBuilder } from '../../template-builder/TemplateBuilder';
import { ConditionalDataConfigurator } from './ConditionalDataConfigurator';

/**
 * Humanize a camelCase / snake_case field name into a Title Case label, e.g.
 * `maxQuantityLast12Months` -> `Max Quantity Last 12 Months`. Used as a fallback
 * label when a column references a field that isn't declared in the list config's
 * selectedFields, so the Select Field dropdown still shows a readable option.
 */
function humanizeFieldName(name: string): string {
	return name
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Za-z])(\d)/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

interface DataSourceConfiguratorProps {
	field: FieldConfiguration;
	configuration?: ListBuilderConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	onPreviewValueChange?: (value: string) => void;
	isModalOpen?: boolean;
}

export function DataSourceConfigurator({
	field,
	configuration,
	onUpdateField,
	onPreviewValueChange,
	isModalOpen = false,
}: DataSourceConfiguratorProps) {
	const colors = useThemeColors();
	const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
	const { listKey } = useListBuilderContext();
	const injectedFields = useInjectedFields(listKey);
	// SPIKE (Tier 1 — PD-14229 follow-up): the full `_object_field` metadata set
	// for every selected object, not just the curated `selectedFields`. This is
	// what lets a tenant add any native column (or `cf:` custom field) straight
	// from the column dropdown, instead of first checking it into the Objects
	// tab. Each field carries its object's label so it groups under that table.
	const [metadataFields, setMetadataFields] = useState<
		Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}>
	>([]);

	useEffect(() => {
		if (configuration?.objects == null || configuration.objects.length === 0)
			return;

		const fetchMetadataFields = async () => {
			const collected: typeof metadataFields = [];
			for (const obj of configuration.objects) {
				const result = await ObjectService.getObjectFields(obj.objectTable);
				if (result != null) {
					for (const field of result) {
						collected.push({
							fieldName: field.fieldName,
							fieldLabel: field.fieldLabel,
							objectTable: obj.objectTable,
							objectLabel: obj.objectLabel,
						});
					}
				}
			}
			setMetadataFields(collected);
		};
		void fetchMetadataFields();
	}, [configuration?.objects]);

	// Get available fields from configuration
	const getAvailableFields = () => {
		if (!configuration?.objects || configuration.objects.length === 0) {
			return [];
		}

		const fields: Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}> = [];

		configuration.objects.forEach((obj) => {
			obj.selectedFields.forEach((field) => {
				fields.push({
					fieldName: field.fieldName,
					fieldLabel: field.fieldLabel,
					objectTable: obj.objectTable,
					objectLabel: obj.objectLabel,
				});
			});
		});

		return fields;
	};

	const availableFields = getAvailableFields();

	// Curated fields (from the list config's selectedFields) — keyed for dedup so
	// a column that's ALSO a raw table field keeps the config's chosen label and
	// position (e.g. order_detail's `quantityOnHand` stays "Qty In Stock" rather
	// than surfacing again as the raw "Quantity On-Hand").
	const curatedKeys = new Set(
		availableFields.map((f) => `${f.objectTable}:${f.fieldName}`),
	);

	// SPIKE (Tier 1): surface every remaining `_object_field` column for each
	// selected object, grouped by its SOURCE TABLE (the object label). Combined
	// with the curated fields above — which carry the same object label — the
	// dropdown reads as ONE section per table ("Order Line Item", "Item"), each
	// listing all of that table's fields, instead of splitting a table across a
	// curated group and a separate "more fields" group. `cf:` custom fields
	// already carry their object's label, so they land in the right section too.
	const extraFields = metadataFields
		.filter((f) => !curatedKeys.has(`${f.objectTable}:${f.fieldName}`))
		.map((f) => ({
			fieldName: f.fieldName,
			fieldLabel: f.fieldLabel,
			objectTable: f.objectTable,
			objectLabel: f.objectLabel,
		}));

	// Combine curated fields, the broader metadata set, and injected fields
	const allAvailableFields = [
		...availableFields,
		...extraFields,
		...injectedFields.map((field) => ({
			fieldName: field.fieldName,
			fieldLabel: field.displayName,
			objectTable: listKey || configuration?.objects?.[0]?.objectTable || '',
			objectLabel: `${configuration?.objects?.[0]?.objectLabel || 'Object'} (Injected)`,
		})),
	];

	// Safety net for the "Select Field" dropdown: guarantee the field this column
	// currently references is always a selectable option. Many TS-authored configs
	// point columns at computed/aggregate fields (e.g. maxQuantityLast12Months) or
	// omit objectTable; those fields aren't in any object's selectedFields, so
	// without this the dropdown renders blank because the saved value matches no
	// option. We surface the current field under the primary object so it shows as
	// selected and remains editable.
	const currentFieldRef =
		field.dataConfig?.type === 'field' ? field.dataConfig.field : undefined;
	if (
		currentFieldRef != null &&
		currentFieldRef.fieldName.length > 0 &&
		!allAvailableFields.some((f) => f.fieldName === currentFieldRef.fieldName)
	) {
		const primaryObject = configuration?.objects?.[0];
		allAvailableFields.push({
			fieldName: currentFieldRef.fieldName,
			fieldLabel: humanizeFieldName(currentFieldRef.fieldName),
			objectTable:
				currentFieldRef.objectTable != null &&
				currentFieldRef.objectTable.length > 0
					? currentFieldRef.objectTable
					: (primaryObject?.objectTable ?? ''),
			objectLabel: primaryObject?.objectLabel ?? 'Fields',
		});
	}

	return (
		<>
			<Card className="mb-4">
				<div className="p-4">
					<div className="flex items-center mb-3">
						<Icon
							icon={faDatabase}
							size="sm"
							color={colors.values.textPrimary}
							className="mr-2"
						/>
						<Text className="text-base font-semibold">Data Source</Text>
					</div>

					{/* Data Source Type Selection */}
					<div className="mb-3">
						<Select
							value={field.dataConfig.type}
							options={[
								{ value: 'field' as DataSourceType, label: 'Database Field' },
								{
									value: 'custom_text' as DataSourceType,
									label: 'Custom Text',
								},
								{ value: 'template' as DataSourceType, label: 'Template' },
								{
									value: 'conditional' as DataSourceType,
									label: 'Conditional',
								},
							]}
							onChange={(value) => {
								const newDataConfig = { type: value as DataSourceType };
								onUpdateField({ dataConfig: newDataConfig });
							}}
						/>
					</div>

					{/* Type-specific configuration */}
					{field.dataConfig.type === 'field' && (
						<div>
							<Text className="block text-xs font-medium mb-1" as="label">
								Select Field
							</Text>
							<div className="mb-2">
								<SelectSearch
									mode="static"
									groups={(() => {
										const grouped = new Map<
											string,
											Array<{ value: string | number; label: string }>
										>();
										allAvailableFields.forEach((f) => {
											const key = f.objectLabel;
											if (!grouped.has(key)) {
												grouped.set(key, []);
											}
											grouped.get(key)?.push({
												value: `${f.objectTable}:${f.fieldName}`,
												label: f.fieldLabel,
											});
										});
										return Array.from(grouped.entries()).map(
											([label, options]) => ({
												key: label,
												label,
												options,
											}),
										);
									})()}
									value={(() => {
										if (field.dataConfig.field == null) return undefined;
										const target = field.dataConfig.field;
										// Prefer an exact objectTable:fieldName match; fall back to
										// matching by fieldName so columns authored without an
										// objectTable (or with a stale one) still resolve instead of
										// rendering blank.
										const match =
											allAvailableFields.find(
												(f) =>
													f.objectTable === target.objectTable &&
													f.fieldName === target.fieldName,
											) ??
											allAvailableFields.find(
												(f) => f.fieldName === target.fieldName,
											);
										return match != null
											? `${match.objectTable}:${match.fieldName}`
											: undefined;
									})()}
									onChange={(value) => {
										const separatorIndex = value.indexOf(':');
										const objectTable = value.substring(0, separatorIndex);
										const fieldName = value.substring(separatorIndex + 1);
										onUpdateField({
											dataConfig: {
												...field.dataConfig,
												field: { objectTable, fieldName },
											},
										});
									}}
									placeholder="Search fields..."
									size="sm"
								/>
							</div>
						</div>
					)}

					{field.dataConfig.type === 'custom_text' && (
						<div>
							<Text className="block text-xs font-medium mb-1" as="label">
								Text Value
							</Text>
							<Input
								type="text"
								value={field.dataConfig.customText || ''}
								onChange={(e) => {
									const text = e.target.value;
									onUpdateField({
										dataConfig: {
											...field.dataConfig,
											customText: text,
										},
									});
									if (onPreviewValueChange) {
										onPreviewValueChange(text);
									}
								}}
								placeholder="Enter custom text..."
								size="sm"
							/>
						</div>
					)}

					{field.dataConfig.type === 'template' && (
						<div>
							<button
								onClick={() => {
									setShowTemplateBuilder(true);
								}}
								className={cn(
									'w-full p-3 border rounded-md transition-colors',
									colors.classes.borderDefault,
									colors.classes.bgSecondary,
									colors.classes.bgPrimaryHover,
								)}
							>
								<div className="flex items-center justify-between">
									<div className="flex-1 text-left">
										<Text className="text-sm font-medium mb-1">
											Template Configuration
										</Text>
										{field.dataConfig.template ? (
											<Text className="text-xs line-clamp-2" color="secondary">
												{field.dataConfig.template}
											</Text>
										) : (
											<Text className="text-xs italic" color="muted">
												Click to configure template...
											</Text>
										)}
									</div>
									<Icon
										icon={faWandMagicSparkles}
										size="sm"
										color={colors.values.primary}
									/>
								</div>
							</button>
						</div>
					)}

					{field.dataConfig.type === 'conditional' && (
						<div>
							<div className="flex items-center mb-3">
								<Icon
									icon={faSitemap}
									size="sm"
									color={colors.values.textPrimary}
									className="mr-2"
								/>
								<Text className="text-sm font-medium">
									Conditional Data Configuration
								</Text>
							</div>
							<ConditionalDataConfigurator
								field={field}
								configuration={configuration}
								onUpdateField={onUpdateField}
								availableFields={allAvailableFields}
							/>
						</div>
					)}

					{/* Default Value for all data source types */}
					<div
						className={cn('mt-4 pt-4 border-t', colors.classes.borderDefault)}
					>
						<Text className="block text-xs font-medium mb-1" as="label">
							Default Value (when null/empty)
						</Text>
						<Input
							type="text"
							value={field.dataConfig.defaultValue ?? ''}
							onChange={(e) => {
								onUpdateField({
									dataConfig: {
										...field.dataConfig,
										defaultValue: e.target.value,
									},
								});
							}}
							placeholder="e.g., -, N/A, Not set"
							size="sm"
						/>
						<Text className="text-xs mt-1" color="secondary">
							This value will be displayed when the field is null or empty
						</Text>
					</div>
				</div>
			</Card>

			{/* Template Builder Modal */}
			{showTemplateBuilder && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className={cn('absolute inset-0', colors.classes.bgOverlay)}
						onClick={() => {
							setShowTemplateBuilder(false);
						}}
					/>
					<div
						className={cn(
							'relative w-full max-w-2xl mx-4 rounded-lg shadow-xl',
							colors.classes.bgPrimary,
						)}
					>
						<TemplateBuilder
							value={field.dataConfig.template || ''}
							onChange={(template) => {
								// Extract fields used in template for dependency tracking
								const templateFields = availableFields
									.filter((f) => template.includes(`{${f.fieldName}}`))
									.map((f) => f.fieldName);

								onUpdateField({
									dataConfig: {
										...field.dataConfig,
										template,
										templateFields,
									},
								});
								setShowTemplateBuilder(false);
							}}
							availableFields={availableFields}
							onClose={() => {
								setShowTemplateBuilder(false);
							}}
						/>
					</div>
				</div>
			)}
		</>
	);
}
